import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Scale, AlertTriangle, Users, Wallet, Ban, RefreshCw, Gavel, Mail } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './TermsOfService.css';

const TermsOfService = () => {
  const lastUpdated = "January 20, 2026";

  const sections = [
    {
      id: 'acceptance',
      icon: FileText,
      title: '1. Acceptance of Terms',
      content: `By accessing or using FTMX ("the Platform," "we," "us," or "our"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not access or use the Platform.

These Terms apply to all users, including visitors, registered users, and anyone who accesses the Platform. We reserve the right to modify these Terms at any time. Continued use of the Platform after changes constitutes acceptance of the modified Terms.

**Eligibility Requirements:**
• You must be at least 18 years old
• You must have the legal capacity to enter into binding agreements
• You must not be prohibited by law from using cryptocurrency services
• You must not be located in a jurisdiction where our services are restricted`
    },
    {
      id: 'services',
      icon: Wallet,
      title: '2. Description of Services',
      content: `FTMX provides a non-custodial cryptocurrency wallet platform that allows users to:

**Core Services:**
• Store, send, and receive various cryptocurrencies
• Connect to decentralized applications (dApps)
• View portfolio balances and transaction history
• Stake supported cryptocurrencies
• Trade digital assets through integrated exchanges

**Important Clarifications:**
• We are a software provider, not a financial institution
• We do not hold custody of your private keys or assets
• We do not provide financial, investment, or tax advice
• All transactions are processed on public blockchain networks
• We do not have the ability to reverse or cancel transactions

**Self-Custody Model:**
You are solely responsible for securing your private keys and recovery phrases. We cannot recover lost keys or restore access to your wallet if you lose your credentials.`
    },
    {
      id: 'user-responsibilities',
      icon: Users,
      title: '3. User Responsibilities',
      content: `As a user of FTMX, you agree to:

**Security Obligations:**
• Safeguard your private keys and recovery phrases
• Use strong, unique passwords for any account features
• Enable available security features (2FA, biometrics)
• Keep your device and software updated
• Never share your credentials with anyone

**Lawful Use:**
• Comply with all applicable laws and regulations
• Not use the Platform for illegal activities
• Not engage in money laundering or terrorist financing
• Accurately report taxable events as required by your jurisdiction
• Verify the legitimacy of transactions and recipients

**Platform Integrity:**
• Not attempt to hack, exploit, or compromise the Platform
• Not interfere with other users' access to the Platform
• Not use automated systems without authorization
• Report bugs and vulnerabilities responsibly
• Provide accurate information when required`
    },
    {
      id: 'prohibited-activities',
      icon: Ban,
      title: '4. Prohibited Activities',
      content: `The following activities are strictly prohibited on FTMX:

**Illegal Activities:**
• Money laundering or proceeds of crime
• Financing terrorism or sanctioned entities
• Tax evasion or fraud
• Purchasing illegal goods or services
• Circumventing international sanctions

**Platform Abuse:**
• Attempting to gain unauthorized access
• Distributing malware or malicious code
• Scraping or harvesting user data
• Creating fake accounts or impersonating others
• Manipulating markets or engaging in wash trading

**Network Attacks:**
• DDoS attacks or service disruption
• Exploiting smart contract vulnerabilities
• Front-running or sandwich attacks
• Spam transactions to congest networks

Violation of these prohibitions may result in immediate termination of access and reporting to relevant authorities.`
    },
    {
      id: 'risks',
      icon: AlertTriangle,
      title: '5. Risk Disclosures',
      content: `Using FTMX and interacting with cryptocurrencies involves significant risks:

**Market Risks:**
• Cryptocurrency values are highly volatile
• You may lose some or all of your investment
• Past performance does not indicate future results
• Market manipulation may occur on some platforms

**Technical Risks:**
• Smart contract bugs or exploits
• Blockchain network congestion or failures
• Software bugs in wallet applications
• Hardware failures or device loss

**Regulatory Risks:**
• Laws regarding cryptocurrencies vary by jurisdiction
• Regulations may change without notice
• Certain activities may become illegal
• Tax obligations may be complex and uncertain

**Security Risks:**
• Phishing attacks and social engineering
• Malware and keyloggers
• Physical theft of devices
• Loss of private keys is irreversible

**YOU ACKNOWLEDGE THAT YOU UNDERSTAND THESE RISKS AND ACCEPT FULL RESPONSIBILITY FOR YOUR DECISIONS.**`
    },
    {
      id: 'intellectual-property',
      icon: Scale,
      title: '6. Intellectual Property',
      content: `**Our Property:**
FTMX and its original content, features, and functionality are owned by us and are protected by international copyright, trademark, patent, and other intellectual property laws.

This includes:
• The FTMX name, logo, and branding
• Platform design and user interface
• Source code and software architecture
• Documentation and educational content

**Your Rights:**
• You retain ownership of your cryptocurrency assets
• You may use the Platform for personal, non-commercial purposes
• You may not copy, modify, or distribute our software
• You may not create derivative works without permission

**Open Source Components:**
Some components of our software may be open source and subject to their respective licenses. These components are identified in our documentation.`
    },
    {
      id: 'limitation-liability',
      icon: Gavel,
      title: '7. Limitation of Liability',
      content: `**Disclaimer of Warranties:**
THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
• Merchantability or fitness for a particular purpose
• Non-infringement of third-party rights
• Accuracy, reliability, or completeness of information
• Uninterrupted or error-free operation

**Limitation of Damages:**
TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR:
• Any indirect, incidental, special, or consequential damages
• Loss of profits, revenue, data, or cryptocurrency assets
• Damages arising from unauthorized access to your wallet
• Damages resulting from third-party actions or services

**Maximum Liability:**
Our total liability for any claims arising from these Terms or your use of the Platform shall not exceed the amount you paid us in the twelve (12) months preceding the claim, or $100 USD, whichever is greater.

**Exceptions:**
Some jurisdictions do not allow limitation of certain warranties or damages. In such cases, our liability is limited to the maximum extent permitted by applicable law.`
    },
    {
      id: 'termination',
      icon: RefreshCw,
      title: '8. Termination',
      content: `**Your Right to Terminate:**
You may stop using FTMX at any time. Since we are non-custodial, your assets remain accessible through your private keys even after you stop using our Platform.

**Our Right to Terminate:**
We may suspend or terminate your access to the Platform:
• If you violate these Terms
• If required by law or regulation
• If we suspect fraudulent or illegal activity
• If necessary for security reasons
• At our discretion with or without notice

**Effect of Termination:**
Upon termination:
• Your right to use the Platform ceases immediately
• You retain access to your assets via your private keys
• Provisions that should survive termination will remain in effect
• We are not liable for any loss resulting from termination

**Data After Termination:**
We may retain certain information as required by law or for legitimate business purposes, in accordance with our Privacy Policy.`
    },
    {
      id: 'dispute-resolution',
      icon: Scale,
      title: '9. Dispute Resolution',
      content: `**Informal Resolution:**
Before initiating formal proceedings, you agree to contact us at legal@ftmx.com to attempt informal resolution. We will work in good faith to resolve disputes within 30 days.

**Arbitration Agreement:**
If informal resolution fails, all disputes shall be resolved through binding arbitration, except where prohibited by law. Arbitration shall be conducted:
• On an individual basis (no class actions)
• In accordance with established arbitration rules
• In the English language
• With decisions being final and binding

**Exceptions:**
Either party may seek injunctive relief in court for:
• Intellectual property infringement
• Unauthorized access or security breaches
• Violations that require immediate remedy

**Governing Law:**
These Terms are governed by the laws of [Jurisdiction], without regard to conflict of law principles.

**Class Action Waiver:**
YOU AGREE TO RESOLVE DISPUTES ON AN INDIVIDUAL BASIS AND WAIVE ANY RIGHT TO PARTICIPATE IN CLASS ACTIONS.`
    },
    {
      id: 'contact',
      icon: Mail,
      title: '10. Contact Information',
      content: `If you have questions or concerns about these Terms of Service, please contact us:

**General Inquiries:**
Email: support@ftmx.com

**Legal Matters:**
Email: legal@ftmx.com

**Security Issues:**
Email: security@ftmx.com

**Response Time:**
We aim to respond to all inquiries within 48-72 business hours.

**Notices:**
Any legal notices to us should be sent via email to legal@ftmx.com with "Legal Notice" in the subject line.

By using FTMX, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.`
    }
  ];

  return (
    <div className="terms-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="terms-hero">
        <div className="hero-glow"></div>
        <div className="container">
          <motion.div 
            className="terms-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="hero-badge">
              <Scale size={14} />
              Legal Agreement
            </span>
            <h1>Terms of Service</h1>
            <p>Please read these terms carefully before using FTMX</p>
            <span className="last-updated">Last Updated: {lastUpdated}</span>
          </motion.div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="terms-nav-section">
        <div className="container">
          <div className="terms-nav">
            {sections.map((section) => (
              <a 
                key={section.id} 
                href={`#${section.id}`}
                className="terms-nav-item"
              >
                <section.icon size={16} />
                <span>{section.title.replace(/^\d+\.\s*/, '')}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="terms-content">
        <div className="container">
          <div className="terms-grid">
            {sections.map((section, index) => (
              <motion.div
                key={section.id}
                id={section.id}
                className="terms-section"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="section-header">
                  <div className="section-icon">
                    <section.icon size={24} />
                  </div>
                  <h2>{section.title}</h2>
                </div>
                <div className="section-content">
                  {section.content.split('\n\n').map((paragraph, i) => (
                    <p key={i} dangerouslySetInnerHTML={{ 
                      __html: paragraph
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\n•/g, '<br/>•')
                        .replace(/\n/g, '<br/>')
                    }} />
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Agreement Box */}
          <motion.div 
            className="terms-agreement"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="agreement-icon">
              <FileText size={32} />
            </div>
            <h3>Agreement Summary</h3>
            <p>
              By using FTMX, you agree to these Terms of Service. 
              You acknowledge that cryptocurrency involves significant risks, 
              that you are responsible for your own security, and that we 
              provide a non-custodial service with no ability to recover lost assets.
            </p>
            <div className="agreement-points">
              <div className="agreement-point">
                <Wallet size={18} />
                <span>Non-custodial service</span>
              </div>
              <div className="agreement-point">
                <AlertTriangle size={18} />
                <span>Understand the risks</span>
              </div>
              <div className="agreement-point">
                <Users size={18} />
                <span>Your responsibility</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfService;
